import {
  Terminal as TerminalClient,
  type TerminalId,
} from "@modules/terminal-client";
import { useCallback, useMemo, useRef } from "react";
import { useTerminalServerUrl } from "@/hooks/useTerminalServerUrl";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  type DevErrorJson,
  useDevServerStore,
} from "@/store/zustand/devServerStore";
import { detectTerminalErrors } from "@/utils/console";
import {
  useDevServerLaunched,
  useLaunchContainer,
  useStartServer,
} from "@/utils/devServerUtils";
import { useActiveMainCanvas, useAutoUpdateRef } from "@/utils/utils";
import { useCanvasStore } from "../Canvas/stores/canvasStore";
import { terminalStyle } from "./theme";

export const DevServer = () => {
  const terminalServerUrl = useTerminalServerUrl();

  const storyboardIdToErrors = useDevServerStore(
    (store) => store.storyboardIdToErrors,
  );

  const devServerLaunchedRef = useAutoUpdateRef(useDevServerLaunched());
  const startServerRef = useAutoUpdateRef(useStartServer());
  const launchContainerRef = useAutoUpdateRef(useLaunchContainer());
  const mainCanvasRef = useAutoUpdateRef(useActiveMainCanvas());
  const anyErrorsAtAllRef = useAutoUpdateRef(
    useMemo(
      () =>
        Object.values(storyboardIdToErrors).filter((errors) => errors?.length)
          .length > 0,
      [storyboardIdToErrors],
    ),
  );
  const restartedServerFromErrorRef = useRef(false);
  const stateRef = useRef({
    isFirstConnection: true,
    errorsData: "",
    convexData: "",
    allEvents: [],
  });
  return (
    <TerminalClient
      url={terminalServerUrl}
      terminalId={"dev-server" as TerminalId}
      {...terminalStyle}
      onData={useCallback(
        (data: Uint8Array) => {
          const state = stateRef.current;

          let messageData: string;
          if (typeof data === "string") {
            messageData = data;
          } else {
            // Binary data from PTY - decode as UTF-8
            const decoder = new TextDecoder("utf-8");
            messageData = decoder.decode(data as unknown as ArrayBuffer);
          }

          // 1 seconds after first message, these are new logs
          setTimeout(() => {
            state.isFirstConnection = false;
          }, 1000);

          // Bug: Oct 22, 2024: the vite dev server doesn't pick up the changes in the codebase
          // Restarting the server fixes this issue
          if (
            messageData?.includes?.(
              "Pre-transform error: Failed to load url",
            ) &&
            messageData?.includes?.("tempobook/")
          ) {
            // Restart server
            if (
              devServerLaunchedRef.current &&
              !restartedServerFromErrorRef.current
            ) {
              restartedServerFromErrorRef.current = true;
              startServerRef.current({
                forceRestart: true,
              });
            }
          } else if (
            messageData?.includes?.(
              "Error: listen EADDRINUSE: address already in use",
            )
          ) {
            // Relaunch container server
            if (!restartedServerFromErrorRef.current) {
              restartedServerFromErrorRef.current = true;
              launchContainerRef
                .current({
                  forceRelaunch: true,
                })
                .then(({ success, error }) => {
                  if (success) {
                    startServerRef.current();
                  }
                });
            }
          }

          // @ts-expect-error TS2345
          state.allEvents.push(messageData);

          // Detect convex login url
          state.convexData += messageData;
          const url = state.convexData.match(
            /Visit https:\/\/auth\.convex\.dev\/activate\?user_code=([A-Z0-9-]+)/,
          );

          const convexUrl = url?.[0]?.split(" ")?.[1];

          if (convexUrl) {
            const userLoggedIn = Boolean(
              useCodebaseStore.getState().conditions["convexInstalled"]?.data
                ?.userLoggedIn,
            );
            if (!userLoggedIn && !state.isFirstConnection) {
              window.open(convexUrl, "_blank");
            }

            if (!state.isFirstConnection) {
              useCanvasStore.getState().setConvexLoginUrl(convexUrl);
            }
            state.convexData = "";
          }

          // Detect terminal errors
          state.errorsData += messageData;
          const terminalErrors = detectTerminalErrors(state.errorsData);

          // Filter out vite errors for storyboards that are deleted
          const deletedStoryboardIds = new Set(
            (mainCanvasRef.current?.storyboards || [])
              .filter((sb: any) => sb.deleted)
              .map((sb: any) => sb.id),
          );

          if (terminalErrors?.errors) {
            terminalErrors.errors = terminalErrors.errors.filter(
              (errors: DevErrorJson) => {
                return !Array.from(deletedStoryboardIds).find((storyboardId) =>
                  errors.message?.includes(storyboardId),
                );
              },
            );
          }

          if (terminalErrors?.errors?.length) {
            // Special case: if it's the first connection, wait 5 seconds and see if
            // any storyboards load, if not, then we can assume the errors from the history
            // still apply (after the project finished loading)
            if (state.isFirstConnection) {
              // Add cleanup of previous interval if exists
              let intervalId: NodeJS.Timeout | null = null;

              const timeoutId = setTimeout(() => {
                clearInterval(intervalId ?? "");
              }, 30000);

              intervalId = setInterval(() => {
                if (devServerLaunchedRef.current) {
                  clearInterval(intervalId ?? "");
                  clearTimeout(timeoutId);
                  if (anyErrorsAtAllRef.current) {
                    useDevServerStore
                      .getState()
                      .addTerminalErrors(terminalErrors.errors);
                  }
                }
              }, 1000);
            } else {
              // Add the errors to the global state and clear allData
              useDevServerStore
                .getState()
                .addTerminalErrors(terminalErrors.errors);
            }
            state.errorsData = ""; // Clear accumulated data after processing
          }
        },
        [
          anyErrorsAtAllRef,
          devServerLaunchedRef,
          launchContainerRef,
          mainCanvasRef,
          startServerRef,
        ],
      )}
    />
  );
};
